<script>
import { carregarIdioma } from 'dashboard/i18n';
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import PlatformBannerNotice from './components/app/PlatformBannerNotice.vue';
import vueActionCable from './helper/actionCable';
import internalChatCable from './helper/internalChatCable';
import AvisoDeMensagemInterna from './routes/dashboard/internal-chat/AvisoDeMensagem.vue';
import MedidorDeTela from 'dashboard/components-next/MedidorDeTela.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePwaAutoUpdate } from 'dashboard/composables/usePwaAutoUpdate';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    PlatformBannerNotice,
    AvisoDeMensagemInterna,
    MedidorDeTela,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      medirTela: (() => {
        try {
          if (window.location.search.includes('medir-tela'))
            sessionStorage.setItem('advup-medir-tela', '1');
          return sessionStorage.getItem('advup-medir-tela') === '1';
        } catch {
          return window.location.search.includes('medir-tela');
        }
      })(),
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      // Audit 2026-05-07 — quando o usuário troca de account (browser back/
      // forward, link interno, ou qualquer mudança de rota que altere
      // accountId), zerar caches indexados por cardId/contactId. Sem isso,
      // IDs da account anterior persistem no Vuex e componentes remontados
      // disparam requests cross-account que retornam 404 protetivo (Vai Pri
      // / ClickHype, 2026-05-07).
      handler(newId, oldId) {
        if (this.currentAccountId) {
          if (oldId && oldId !== newId) {
            this.$store.dispatch('pipelineCards/resetForAccountSwitch');
            this.$store.dispatch('pipelineCardSequences/clearAll');
          }
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
    usePwaAutoUpdate();
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    // Os idiomas além de português e inglês chegam sob demanda
    // (dashboard/i18n): primeiro as mensagens, depois a troca.
    async setLocale(locale) {
      if (await carregarIdioma(this.$root.$i18n, locale)) {
        this.$root.$i18n.locale = locale;
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      vueActionCable.init(this.store, pubsubToken);
      // O CABO DO CHAT INTERNO SOBE COM O APP, e não com a tela do chat.
      //
      // Ele era inicializado dentro do `InternalChatView`, então quem estivesse
      // no Dashboard, no Pipeline ou na Agenda nunca abria o
      // `AccountInternalChatChannel` — o `unread_increment` não chegava e a
      // bolinha do ícone só se mexia depois de visitar o chat interno ou
      // recarregar a página. Relatado por um usuário do A&T em 2026-08-06
      // ("só vi a bolinha depois de apertar F5").
      //
      // A bolinha mora na barra lateral, que é global; a fonte dela precisa ser
      // global também. `init` é idempotente, então a chamada que já existe na
      // tela do chat continua inofensiva.
      internalChatCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration => {
        // Check for SW updates
        registration.update();
        // Re-subscribe to push if already subscribed
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        });
      });
    },
  },
};
</script>

<template>
  <!-- advup PWA mobile (2026-07-27) — achado real do Victor em device
       físico: vão vazio embaixo da bottom nav (visível como faixa branca
       antes do fix de cor, agora dark mas ainda "sobrando"). Causa raiz:
       h-screen = 100vh, e no Safari mobile 100vh é calculado pro MAIOR
       viewport possível (barra de endereço recolhida), não o visível de
       verdade — sobra um vão do tamanho da diferença toda vez que a barra
       está expandida. h-dvh (dynamic viewport height, Tailwind 3.4+)
       acompanha o viewport visível de verdade em tempo real. Idêntico a
       h-screen em desktop/qualquer navegador sem barra dinâmica. -->
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-dvh min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <PlatformBannerNotice v-if="currentAccountId" />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
    <!-- Aviso de mensagem do chat interno (estilo MSN). Fica aqui, e não na
         tela do chat, porque o ponto é avisar quem está em OUTRA tela. -->
    <AvisoDeMensagemInterna v-if="currentAccountId" />
    <!-- advup PWA: ?medir-tela mostra as medidas reais do aparelho -->
    <MedidorDeTela v-if="medirTela" />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
